/* ══════════════════════════════════════════════════════════════
   Runtime Detection Service — Detect execution environment
   Identifies browser, Tauri desktop, PWA, mobile, and SSR
   ══════════════════════════════════════════════════════════════ */

export type RuntimeEnv = 'browser' | 'tauri' | 'pwa' | 'ssr';
export type Platform = 'desktop' | 'mobile' | 'tablet';

export interface RuntimeInfo {
    env: RuntimeEnv;
    platform: Platform;
    isTauri: boolean;
    isPWA: boolean;
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
    isOnline: boolean;
    userAgent: string;
    screenWidth: number;
    screenHeight: number;
    touchSupport: boolean;
    serviceWorkerSupported: boolean;
}

/** Detect if running inside Tauri */
export function isTauri(): boolean {
    return typeof window !== 'undefined' && '__TAURI__' in window;
}

/** Detect if running as installed PWA */
export function isPWA(): boolean {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches ||
           (window.navigator as any).standalone === true;
}

/** Detect if on mobile device */
export function isMobile(): boolean {
    if (typeof window === 'undefined') return false;
    return /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           window.innerWidth < 768;
}

/** Detect if on tablet */
export function isTablet(): boolean {
    if (typeof window === 'undefined') return false;
    return /iPad|Android/i.test(navigator.userAgent) &&
           window.innerWidth >= 768 && window.innerWidth <= 1024;
}

/** Detect if SSR/server context */
export function isSSR(): boolean {
    return typeof window === 'undefined';
}

/** Get current runtime environment */
export function getRuntimeEnv(): RuntimeEnv {
    if (isSSR()) return 'ssr';
    if (isTauri()) return 'tauri';
    if (isPWA()) return 'pwa';
    return 'browser';
}

/** Get platform type */
export function getPlatform(): Platform {
    if (isTablet()) return 'tablet';
    if (isMobile()) return 'mobile';
    return 'desktop';
}

/** Get full runtime info */
export function getRuntimeInfo(): RuntimeInfo {
    const env = getRuntimeEnv();
    const platform = getPlatform();

    return {
        env,
        platform,
        isTauri: env === 'tauri',
        isPWA: env === 'pwa',
        isMobile: platform === 'mobile',
        isTablet: platform === 'tablet',
        isDesktop: platform === 'desktop',
        isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        screenWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
        screenHeight: typeof window !== 'undefined' ? window.innerHeight : 0,
        touchSupport: typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0),
        serviceWorkerSupported: typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
    };
}

/** Subscribe to online/offline changes */
export function onConnectivityChange(callback: (online: boolean) => void): () => void {
    const onOnline = () => callback(true);
    const onOffline = () => callback(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
        window.removeEventListener('online', onOnline);
        window.removeEventListener('offline', onOffline);
    };
}
